import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Paperclip, Smile } from 'lucide-react';
import { lazy, Suspense, useCallback, useEffect, useRef, useState, } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import { useRecoilState, useRecoilValue } from 'recoil';
import { LoadingIcon } from '@/components/Icons/LoadingIcon';
import { SendMessageIcon } from '@/components/Icons/SendMessageIcon';
import { Button } from '@/components/ui/button';
import { NEW_MAX_CHARS, MAX_TYPING_DURATION_MS } from '@/config/constants';
import { useDebounce } from '@/hooks/useDebounce';
import { useFileDropzone } from '@/hooks/useFileDropzone';
import { useSocket } from '@/hooks/useSocket';
import { cn } from '@/lib/utils';
import { canUploadFileSelector, themeSelector, widgetPositionSelector, } from '@/state/config';
import { disableInputSelector, filesToUploadLengthSelector, filesToUploadState, isUploadingFileState, isWidgetOpenState, } from '@/state/session';
import { connectionState } from '@/state/socket';
import { getFileTypeFromMimeType, KEYS, sleep } from '@/util/utils';
import { FilesCarousel } from './FilesCarousel/FilesCarousel';
import { useEmojiPicker } from './useEmojiPicker';
// Lazy load the emoji picker
const EmojiPicker = lazy(() => import('emoji-picker-react'));
export const MessageInput = () => {
    const { t } = useTranslation();
    const theme = useRecoilValue(themeSelector);
    // useState
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [disableInput, setDisableInput] = useState(false);
    // useRef
    const inputRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const { isEmojiPickerOpen, setIsEmojiPickerOpen, handleEmojiClick, saveCursorPosition, } = useEmojiPicker(inputRef, setInput);
    //useRecoilState
    const [filesToUpload, setFilesToUpload] = useRecoilState(filesToUploadState);
    // useRecoilValue
    const connectionStatus = useRecoilValue(connectionState);
    const shouldDisableInput = useRecoilValue(disableInputSelector);
    const isWidgetOpen = useRecoilValue(isWidgetOpenState);
    const filesToUploadLength = useRecoilValue(filesToUploadLengthSelector);
    const canUploadFile = useRecoilValue(canUploadFileSelector);
    const isUploading = useRecoilValue(isUploadingFileState);
    const { sendMessage, sendCompose } = useSocket();
    // useDebounce
    const debouncedInput = useDebounce(String(input), MAX_TYPING_DURATION_MS);
    const widgetPosition = useRecoilValue(widgetPositionSelector);
    const { open, getInputProps } = useFileDropzone();
    useClickAway(emojiPickerRef, async () => {
        await sleep(100);
        setIsEmojiPickerOpen(false);
    });
    // Disable the input for 1 second if there is a Virtual Agent present.
    // This gives time to the brain to respond to user input. This is a hacky but
    // if we don't do this, the user can type many times before the brain responds.
    const disableInputTimeout = useCallback(() => {
        let timer;
        if (shouldDisableInput) {
            setDisableInput(true);
            timer = setTimeout(() => {
                setDisableInput(false);
            }, MAX_TYPING_DURATION_MS);
        }
        return () => clearTimeout(timer);
    }, [shouldDisableInput]);
    useEffect(() => {
        if (isTyping) {
            setIsTyping(false);
            sendCompose('stop');
        }
        // We check `isTyping` because we don't want to send a `stop` event if the user was not typing.
        // Don't add it to the hook dependencies, it will cause an infinite loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedInput, sendCompose]);
    // Set the focus on the element 50ms after is shown
    useEffect(() => {
        if (isWidgetOpen &&
            inputRef.current &&
            widgetPosition !== 'embed' &&
            filesToUploadLength === 0) {
            setTimeout(() => {
                var _a;
                (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 100);
        }
    }, [filesToUpload, filesToUploadLength, isWidgetOpen, widgetPosition]);
    const handleKeyEvent = useCallback((event) => {
        // In some cases, we are uploading a large file that takes time so we need to disable the "keyevent"
        if (isUploading) {
            return;
        }
        if ((filesToUploadLength > 0 || input.trim().length > 0) &&
            (event.key === KEYS.ENTER || event.type === 'click')) {
            event.preventDefault();
            sendMessage({
                text: input === null || input === void 0 ? void 0 : input.trim(),
                attachments: filesToUpload.map((f) => {
                    return {
                        type: getFileTypeFromMimeType(f.content_type),
                        id: f.file_id,
                        mime_type: f.content_type,
                        title: f.original_name,
                        filename: f.filename,
                    };
                }),
            });
            disableInputTimeout();
            if (inputRef.current) {
                inputRef.current.textContent = '';
                setInput('');
            }
            if (filesToUpload.length > 0) {
                setFilesToUpload([]);
            }
        }
    }, [
        input,
        isUploading,
        filesToUploadLength,
        sendMessage,
        filesToUpload,
        disableInputTimeout,
        setFilesToUpload,
    ]);
    const handleInputEvent = useCallback((event) => {
        handleKeyEvent(event);
    }, [handleKeyEvent]);
    /**
     * Prevents input/paste of more characters than allowed.
     * When deleting text , allow placeholder to be shown again in Firefox and Safari
     */
    const handleLimitInputChange = useCallback(() => {
        var _a, _b, _c;
        if (((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.textContent) &&
            ((_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.textContent.length) >= NEW_MAX_CHARS) {
            inputRef.current.textContent = (_c = inputRef.current) === null || _c === void 0 ? void 0 : _c.textContent.slice(0, NEW_MAX_CHARS);
            const selection = window.getSelection();
            const range = document.createRange();
            selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
            range.selectNodeContents(inputRef.current);
            range.collapse(false);
            selection === null || selection === void 0 ? void 0 : selection.addRange(range);
            inputRef.current.focus();
        }
        const element = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current;
        if (!(element === null || element === void 0 ? void 0 : element.textContent)) {
            const breakTag = element === null || element === void 0 ? void 0 : element.getElementsByTagName('br')[0];
            if (breakTag) {
                element === null || element === void 0 ? void 0 : element.removeChild(breakTag);
            }
        }
    }, []);
    const handleKeyUp = useCallback((event) => {
        if ((filesToUploadLength > 0 || input.trim().length > 0) &&
            event.key === KEYS.ENTER) {
            handleInputEvent(event);
        }
    }, [filesToUploadLength, handleInputEvent, input]);
    const handleInputChange = useCallback(() => {
        var _a;
        handleLimitInputChange();
        if (!isTyping) {
            sendCompose('start');
            setIsTyping(true);
        }
        setInput(((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.textContent) || '');
    }, [handleLimitInputChange, isTyping, sendCompose]);
    const handleKeyDown = useCallback((event) => {
        if (event.key === KEYS.ENTER) {
            event.preventDefault();
        }
    }, []);
    const handlePaste = useCallback((e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text/plain');
        document.execCommand('insertText', false, text.trim());
    }, []);
    const handleAttachFileClick = useCallback(() => {
        open();
    }, [open]);
    const handleRemoveFile = useCallback((fileId) => {
        setFilesToUpload((prev) => prev.filter((file) => file.file_id !== fileId));
    }, [setFilesToUpload]);
    const areAllFilesUploaded = filesToUpload.every((f) => f.upload_url);
    const emojiPickerTheme = theme === 'dark' ? 'dark' : null;
    return (_jsxs("div", { className: cn('dark:border-input bg-background relative z-1 min-h-24 w-full rounded-xl border border-gray-200'), children: [isEmojiPickerOpen && (_jsx("div", { ref: emojiPickerRef, className: "absolute right-0 bottom-full left-0 z-10 mb-2", children: _jsx(Suspense, { fallback: _jsx("div", { className: "bg-background flex h-96 w-full items-center justify-center rounded-lg", children: _jsx(LoadingIcon, { size: "small" }) }), children: _jsx(EmojiPicker, { onEmojiClick: handleEmojiClick, width: "100%", autoFocusSearch: false, theme: emojiPickerTheme, previewConfig: {
                            showPreview: false,
                        } }) }) })), _jsx("div", { className: "relative flex max-h-50 items-start pb-[40px]", children: _jsx("div", { ref: inputRef, className: cn("message-input" /* TestClassName.MessageInput */, 'text-foreground max-h-40 flex-1 appearance-none overflow-y-auto border-0 p-3 leading-6 text-wrap break-words outline-none'), style: {
                        scrollbarWidth: 'none',
                    }, "aria-label": "input", role: "textbox", tabIndex: 0, "data-placeholder": t('input_placeholder'), contentEditable: connectionStatus === 'online' && !disableInput, onKeyUp: handleKeyUp, onKeyDown: handleKeyDown, onInput: handleInputChange, onPaste: handlePaste }) }), _jsxs("div", { className: "absolute right-0 bottom-3 left-0 flex h-[28px] w-full justify-between px-3", children: [filesToUploadLength > 0 ? (_jsx(FilesCarousel, { filesToUpload: filesToUpload, handleRemoveFile: handleRemoveFile })) : (_jsx(_Fragment, { children: _jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Button, { size: "sm", className: "h-7 w-7 min-w-7", variant: "ghost-neutral", onClick: () => {
                                        saveCursorPosition();
                                        setIsEmojiPickerOpen(true);
                                    }, children: _jsx(Smile, { size: 16 }) }), filesToUploadLength == 0 && canUploadFile && (_jsx(_Fragment, { children: _jsxs(Button, { size: "sm", className: "h-7 w-7 min-w-7", variant: "ghost-neutral", onKeyUp: handleAttachFileClick, onClick: handleAttachFileClick, onTouchStart: handleAttachFileClick, children: [_jsx("input", Object.assign({}, getInputProps())), _jsx(Paperclip, { size: 16 })] }) }))] }) })), _jsxs(Button, { size: "sm", className: "absolute right-3 h-7 w-7 min-w-7 cursor-pointer p-0 [&_svg]:h-6 [&_svg]:w-6", variant: "ghost-neutral", onKeyUp: handleInputEvent, onClick: handleInputEvent, children: [disableInput && (_jsx(LoadingIcon, { size: "small", variant: theme === 'dark' ? 'light' : 'primary' })), input.length > 0 ||
                                (filesToUploadLength > 0 && areAllFilesUploaded) ? (_jsx(SendMessageIcon, {})) : null] })] })] }));
};
